/**
 * Product ID utilities for consistent tracking across Facebook events and XML feed
 * These IDs must match the ones generated in the Google Merchant Center XML feed
 */

import { ServiceType, PlanType } from '../types/api';

/**
 * Generate product ID that matches the XML feed format
 * This ensures consistency between Facebook tracking events and Google Merchant Center
 */
export function generateProductId(
  serviceType: ServiceType,
  planType: PlanType,
  subscriptionType: 'monthly' | 'yearly' | 'quarterly' | 'annual' | 'biannual' | 'trial',
  planId?: string | number,
  isTrial: boolean = false
): string {
  // Use shorter abbreviations to keep under 50 character limit (same as XML feed)
  const serviceMap: { [key: string]: string } = {
    flower: 'FL',
    fruit: 'FR',
    cat_grass: 'CG'
  };
  
  const planMap: { [key: string]: string } = {
    standard: 'STD',
    premium: 'PRM'
  };
  
  const frequencyMap: { [key: string]: string } = {
    monthly: 'M',
    quarterly: 'Q',
    biannual: 'B',
    annual: 'A',
    yearly: 'A', // Map yearly to annual
    trial: 'T'
  };
  
  const service = serviceMap[serviceType] || 'FL';
  const planCode = planMap[planType] || 'STD';
  const freq = frequencyMap[subscriptionType] || 'M';
  
  // Include unique identifier to prevent duplicates
  // Use first 8 characters of UUID/ID to keep within character limit
  const planIdStr = planId ? String(planId).substring(0, 8) : 'DEFAULT';
  
  // Add trial suffix if this is a trial product
  const trialSuffix = isTrial ? '_TRIAL' : '';
  
  // Create unique ID including plan identifier
  const baseId = `PIIKA_${service}_${planCode}_${freq}_${planIdStr}${trialSuffix}`;
  
  // Truncate if too long (max 50 chars as per Google requirements)
  return baseId.length > 50 ? baseId.substring(0, 50) : baseId;
}

/**
 * Generate product name for tracking events
 */
export function generateProductName(
  serviceType: ServiceType,
  planType: PlanType,
  subscriptionType: 'monthly' | 'yearly' | 'quarterly' | 'annual' | 'biannual' | 'trial',
  isTrial: boolean = false
): string {
  const serviceMap: { [key: string]: string } = {
    flower: 'Flower',
    fruit: 'Fruit',
    cat_grass: 'Cat Grass'
  };
  
  const serviceName = serviceMap[serviceType] || 'Flower';
  const planName = planType.charAt(0).toUpperCase() + planType.slice(1);
  const frequencyName = subscriptionType === 'yearly' ? 'Annual' : 
                       subscriptionType.charAt(0).toUpperCase() + subscriptionType.slice(1);
  const trialPrefix = isTrial ? 'Trial ' : '';
  
  return `${trialPrefix}${planName} ${serviceName} Subscription - ${frequencyName}`;
}

/**
 * Generate product category for tracking
 */
export function generateProductCategory(serviceType: ServiceType): string {
  const categoryMap: { [key: string]: string } = {
    flower: 'Subscription',
    fruit: 'Subscription',
    cat_grass: 'Subscription'
  };
  
  return categoryMap[serviceType] || 'Subscription';
}

/**
 * Generate content IDs array for Facebook events
 * This should match the product IDs used in Google Merchant Center
 */
export function generateContentIds(
  serviceType: ServiceType,
  planType: PlanType,
  subscriptionType: 'monthly' | 'yearly' | 'quarterly' | 'annual' | 'biannual' | 'trial',
  planId?: string | number,
  isTrial: boolean = false
): string[] {
  return [generateProductId(serviceType, planType, subscriptionType, planId, isTrial)];
} 